import React from 'react';
import { motion } from 'framer-motion';
import { GripVertical, Trash2, Copy } from 'lucide-react';
import ImageUpload from '../ImageUpload';

const QuestionCard = ({ 
  question, 
  index, 
  isSelected, 
  onSelect, 
  onUpdate, 
  onDelete, 
  onDuplicate,
  isDragging,
  theme
}) => {
  
  const handleTextChange = (e) => {
    onUpdate(question.id, { text: e.target.value });
  };

  const handleRequiredChange = (e) => {
    onUpdate(question.id, { required: e.target.checked });
  };

  const handlePointsChange = (e) => {
    onUpdate(question.id, { points: parseInt(e.target.value) || 0 });
  };

  const handleOptionChange = (optionIndex, value) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = value;
    onUpdate(question.id, { options: newOptions });
  };

  const handleAddOption = () => {
    const newOptions = [...question.options, `Option ${question.options.length + 1}`];
    onUpdate(question.id, { options: newOptions });
  };

  const handleRemoveOption = (optionIndex) => {
    const newOptions = question.options.filter((_, idx) => idx !== optionIndex);
    onUpdate(question.id, { options: newOptions });
  };

  const handleImageUpload = (optionIndex, imageUrl) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = imageUrl;
    onUpdate(question.id, { options: newOptions });
  };

  const handleImageRemove = (optionIndex) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = '';
    onUpdate(question.id, { options: newOptions });
  };

  const handleAddImageOption = () => {
    const newOptions = [...question.options, ''];
    onUpdate(question.id, { options: newOptions });
  };

  // Get appropriate placeholder based on question type
  const getInputPlaceholder = () => {
    switch (question.type) {
      case 'short-text': return 'Short text answer...';
      case 'long-text': return 'Long text answer...';
      case 'text': return 'Text answer will appear here...';
      case 'email': return 'Email input will appear here...';
      case 'number': return 'Number input will appear here...';
      case 'phone': return 'Phone input will appear here...';
      case 'url': return 'https://example.com';
      default: return 'Input will appear here...';
    }
  };

  // Use default theme if not provided
  const themeColors = theme || {
    cardBg: '#FFFFFF',
    border: '#E5E7EB',
    borderFocus: '#0066FF',
    text: '#111827',
    textSecondary: '#6B7280',
    primary: '#0066FF',
    inputBg: '#F9FAFB'
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        scale: isDragging ? 1.05 : 1,
        boxShadow: isDragging ? '0 10px 30px rgba(0,0,0,0.1)' : 'none'
      }}
      exit={{ opacity: 0, y: -20 }}
      className={`rounded-xl border-2 transition-all ${
        isDragging ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
      style={{
        backgroundColor: themeColors.cardBg,
        borderColor: isSelected ? themeColors.primary : themeColors.border
      }}
      onClick={onSelect}
    >
      <div className="p-4 sm:p-5 md:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            {/* Drag Handle */}
            <div
              className="p-0.5 sm:p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <GripVertical size={14} className="sm:w-4 sm:h-4" />
            </div>

            {/* Question Number */}
            <span
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm font-medium flex-shrink-0"
              style={{
                backgroundColor: `${themeColors.primary}1A`,
                color: themeColors.primary
              }}
            >
              Q{index + 1}
            </span>

            {/* Question Type Badge */}
            <span
              className="px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm font-medium capitalize truncate"
              style={{
                backgroundColor: `${themeColors.primary}1A`,
                color: themeColors.primary
              }}
            >
              {question.type.replace('-', ' ')}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(question.id);
              }}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <Copy size={14} className="sm:w-4 sm:h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(question.id);
              }}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
            >
              <Trash2 size={14} className="sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>

        {/* Question Text Input */}
        <input
          type="text"
          placeholder="Enter your question here..."
          value={question.text}
          onChange={handleTextChange}
          className="w-full text-base sm:text-lg font-semibold bg-transparent border-none outline-none placeholder-gray-400 mb-3 sm:mb-4"
          style={{ color: themeColors.text }}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Question Input Preview */}
        <div className="mb-4">
          {/* Short Text Input */}
          {question.type === 'short-text' && (
            <input
              type="text"
              placeholder={getInputPlaceholder()}
              maxLength={100}
              className="w-full px-3 py-2 border rounded-lg text-gray-500"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            />
          )}

          {/* Long Text Input */}
          {question.type === 'long-text' && (
            <textarea
              placeholder={getInputPlaceholder()}
              rows={4}
              className="w-full px-3 py-2 border rounded-lg text-gray-500 resize-none"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            />
          )}

          {/* Legacy Text, Email, Number, Phone, URL Inputs */}
          {(question.type === 'text' || question.type === 'email' || question.type === 'number' || question.type === 'phone' || question.type === 'url') && (
            <input
              type="text"
              placeholder={getInputPlaceholder()}
              className="w-full px-3 py-2 border rounded-lg text-gray-500"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            />
          )}

          {/* Date Input */}
          {question.type === 'date' && (
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg text-gray-500"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            />
          )}

          {/* Time Input */}
          {question.type === 'time' && (
            <input
              type="time"
              className="w-full px-3 py-2 border rounded-lg text-gray-500"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            />
          )}
          
          {/* Multiple Choice & Checkbox & Dropdown & Ranking Options */}
          {(question.type === 'multiple-choice' || question.type === 'checkbox' || question.type === 'dropdown' || question.type === 'ranking') && (
            <div className="space-y-2">
              {question.options?.map((option, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  {question.type === 'multiple-choice' && (
                    <input
                      type="radio"
                      disabled
                      className="text-blue-500"
                      style={{ accentColor: themeColors.primary }}
                    />
                  )}
                  {question.type === 'checkbox' && (
                    <input
                      type="checkbox"
                      disabled
                      className="text-blue-500"
                      style={{ accentColor: themeColors.primary }}
                    />
                  )}
                  {question.type === 'dropdown' && (
                    <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                  )}
                  {question.type === 'ranking' && (
                    <GripVertical size={16} className="text-gray-400" />
                  )}
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(idx, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                    style={{
                      backgroundColor: themeColors.cardBg,
                      borderColor: themeColors.border,
                      color: themeColors.text
                    }}
                    placeholder={`Option ${idx + 1}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveOption(idx);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500"
                    disabled={question.options.length <= 2}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddOption();
                }}
                className="text-sm font-medium hover:opacity-80"
                style={{ color: themeColors.primary }}
              >
                + Add Option
              </button>
            </div>
          )}

          {/* Yes/No Options */}
          {question.type === 'yes-no' && (
            <div className="flex gap-3">
              <button
                className="flex-1 px-4 py-3 border-2 rounded-lg font-medium text-sm transition-all"
                style={{
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg,
                  color: themeColors.text
                }}
                disabled
              >
                Yes
              </button>
              <button
                className="flex-1 px-4 py-3 border-2 rounded-lg font-medium text-sm transition-all"
                style={{
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg,
                  color: themeColors.text
                }}
                disabled
              >
                No
              </button>
            </div>
          )}

          {/* Gender Dropdown */}
          {question.type === 'gender' && (
            <select
              className="w-full px-3 py-2 border rounded-lg text-gray-500"
              style={{
                backgroundColor: themeColors.inputBg,
                borderColor: themeColors.border
              }}
              disabled
            >
              <option>Select gender</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
              <option>Prefer not to say</option>
            </select>
          )}

          {/* Linear Scale Preview */}
          {question.type === 'scale' && (
            <div className="space-y-3">
              <input
                type="range"
                min="1"
                max="10"
                defaultValue="5"
                className="w-full"
                style={{ accentColor: themeColors.primary }}
                disabled
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
                <span>6</span>
                <span>7</span>
                <span>8</span>
                <span>9</span>
                <span>10</span>
              </div>
            </div>
          )}

          {/* Rating Preview */}
          {question.type === 'rating' && (
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <div 
                  key={star} 
                  className="w-8 h-8 flex items-center justify-center text-lg border rounded text-gray-400"
                  style={{ 
                    borderColor: themeColors.border,
                    backgroundColor: themeColors.inputBg
                  }}
                >
                  ⭐
                </div>
              ))}
            </div>
          )}
          
          {/* Image Choice Preview */}
          {question.type === 'image' && (
            <div className="grid grid-cols-2 gap-3">
              {question.options?.map((imageUrl, idx) => (
                <div
                  key={idx}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ImageUpload
                    existingImage={imageUrl || null}
                    onImageUploaded={(url) => handleImageUpload(idx, url)}
                    onImageRemoved={() => handleImageRemove(idx)}
                  />
                </div>
              ))}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddImageOption();
                }}
                className="aspect-square border-2 border-dashed rounded-xl flex items-center justify-center text-gray-400 text-sm hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
                style={{
                  backgroundColor: `${themeColors.primary}08`,
                  borderColor: themeColors.border,
                  minHeight: '192px'
                }}
              >
                + Add Image
              </button>
            </div>
          )}
        </div>

        {/* Settings */}
        <div 
          className="flex items-center justify-between pt-4 border-t"
          style={{ borderColor: themeColors.border }}
        >
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm" style={{ color: themeColors.textSecondary }}>
              <input
                type="checkbox"
                checked={question.required}
                onChange={handleRequiredChange}
                className="rounded"
                style={{ accentColor: themeColors.primary }}
                onClick={(e) => e.stopPropagation()}
              />
              Required
            </label>
            
            <div className="flex items-center gap-2 text-sm" style={{ color: themeColors.textSecondary }}>
              <span>Points:</span>
              <input
                type="number"
                value={question.points}
                onChange={handlePointsChange}
                className="w-16 px-2 py-1 border rounded text-center"
                style={{ 
                  borderColor: themeColors.border,
                  backgroundColor: themeColors.inputBg
                }}
                min="0"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Character Counter for Text Inputs */}
          {(question.type === 'text' || question.type === 'email') && (
            <div className="text-xs" style={{ color: themeColors.textMuted }}>
              {question.text.length}/500
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default QuestionCard;